import { defaultClient } from '@mint-sdk/api-core';
import z from 'zod';

import {
  AllUserListResponseSchema,
  LegacyUserListResponseSchema,
  PaginatedUserListResponseSchema,
  UserColumnsResponseSchema,
  UserLiteListResponseSchema,
  UserSchema,
  UserShortListResponseSchema,
} from './schemas.js';

export type {
  LegacyUserItem,
  UserColumn,
  UserItem,
  UserLiteItem,
  UserShortItem,
} from './schemas.js';

export const listUsers = defaultClient.query({
  url: 'authentication/v1/users/?is_deleted' as const,
  method: 'GET',
  responseSchema: LegacyUserListResponseSchema,
  processResponse(data) {
    return data;
  },
});

export const listUsersShort = defaultClient.query({
  url: 'authentication/v1/users-short/' as const,
  method: 'GET',
  querySchema: z.object({
    search: z.string().optional(),
  }),
  responseSchema: UserShortListResponseSchema,
  processResponse(data) {
    return data;
  },
});

export const listUsersLite = defaultClient.query({
  url: 'authentication/v1/users/lite' as const,
  method: 'GET',
  responseSchema: UserLiteListResponseSchema,
  processResponse(data) {
    return data.data.items;
  },
  querySchema: z.object({
    id: z.number().nullable().optional(),
    featureName: z.string().optional(),
    accessLevel: z.string().optional(),
    campaignId: z.number().nullable().optional(),
    mediaPlanId: z.string().nullable().optional(),
    includeOwners: z.boolean().optional(),
  }),
  snakeCaseQueryParams: true,
});

export const resendUserInvitation = defaultClient.mutation({
  url: 'authentication/v1/users/send-invitation/' as const,
  method: 'POST',
  requestSchema: z.object({
    id: z.union([z.number(), z.string()]),
  }),
  // the endpoint returns no payload we consume on the FE the caller only cares about success or error of the mutation.
  responseSchema: z.unknown(),
});

// MINT 2.0 API endpoint for listing users.
export const listPaginatedUsers = defaultClient.query({
  url: 'authentication/v2/users/' as const,
  method: 'GET',
  responseSchema: PaginatedUserListResponseSchema,
  processResponse(data) {
    return data;
  },
  querySchema: z.object({
    search: z.string().optional(),
    limit: z.number().optional(),
    offset: z.number().optional(),
    ordering: z.string().optional(),
    includeInheritedTags: z.boolean().optional(),
    excludeTags: z.boolean().optional(),
  }),
  snakeCaseQueryParams: true,
});

export const listUsersColumns = defaultClient.query({
  url: 'authentication/v2/users/ag-grid/columns/' as const,
  method: 'GET',
  responseSchema: UserColumnsResponseSchema,
  processResponse(data) {
    return data.data.items;
  },
});

export const resendUserInvitationV2 = defaultClient.mutation({
  url: 'authentication/v2/users/send-invitation/' as const,
  method: 'POST',
  requestSchema: z.object({
    id: z.union([z.number(), z.string()]),
  }),
  responseSchema: UserSchema,
});

export const switchUserStatusV2 = defaultClient.mutation({
  url: 'authentication/v2/users/switch-status/' as const,
  method: 'POST',
  requestSchema: z.object({
    id: z.union([z.number(), z.string()]),
  }),
  responseSchema: UserSchema,
});

export const createUserV2 = defaultClient.mutation({
  url: 'authentication/v2/users/' as const,
  method: 'POST',
  clientOptions: { headers: { 'Content-Type': 'multipart/form-data' } },
  requestSchema: z.instanceof(FormData),
  responseSchema: z.object({
    id: z.union([z.number(), z.string()]).optional(),
    addInIdp: z.boolean().optional(),
  }),
});

export const editUserV2 = defaultClient.mutation({
  url: 'authentication/v2/users/$id/' as const,
  method: 'PATCH',
  urlParamsSchema: z.object({ id: z.union([z.number(), z.string()]) }),
  // Partial on purpose: the context menu patches only the role, the edit modal sends the rest.
  requestSchema: z
    .object({
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
      mobileNumber: z.string(),
      officeNumber: z.string(),
      role: z.union([z.number(), z.string()]),
      tagsV2: z.array(z.string()),
    })
    .partial(),
  responseSchema: UserSchema,
});

export const deleteUserV2 = defaultClient.mutation({
  url: 'authentication/v2/users/$id/' as const,
  method: 'DELETE',
  urlParamsSchema: z.object({ id: z.union([z.number(), z.string()]) }),
  responseSchema: z.unknown(),
});

export const listAllUsers = defaultClient.query({
  url: 'authentication/v2/users/' as const,
  method: 'GET',
  responseSchema: AllUserListResponseSchema,
  processResponse(data) {
    return data;
  },
  querySchema: z.object({
    search: z.string().optional(),
    limit: z.literal(0), // Set limit to 0 to fetch all users
    offset: z.number().optional(),
    ordering: z.string().optional(),
  }),
});
